import { useParams } from "react-router-dom"
import { pluralize } from "web/services/inflector"

import {
  useCurrentUser,
  useFeatureFlag,
  useTopicRecordBulk,
  useTopics,
} from "web/hooks/data"
import { Dialogs, useDialogModal } from "web/hooks/modals"

import { ObjectList } from "web/components"
import useViewOrEditTopicModal from "web/components/ViewOrEdit/ViewOrEditTopic"
import routes from "web/routes/constants"

import { TrashIcon } from "@heroicons/react/24/outline"

function TopicObjectList({
  subjectType,
  marketId,
  topic,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const currentUser = useCurrentUser()
  const topicRecordBulk = useTopicRecordBulk()
  const confirmDeleteModal = useDialogModal(
    Dialogs.ConfirmBulkDelete(`${topic.name} records`)
  )

  const useSSRTopicRecordModals = useFeatureFlag("ssr_topic_record_modals")

  const viewOrEditTopicModal = useViewOrEditTopicModal({
    subjectType,
    topic,
  })

  async function handleTopicRecordClick(record, list) {
    if (useSSRTopicRecordModals) {
      window.Turbo.visit(
        routes.ssrEditTopicRecord({
          subjectType: subjectType,
          subjectId: record.subject_id,
          topicName: topic.name,
          id: record.id,
          isCrossSubject: true,
        }),
        {
          frame: "modal",
        }
      )
    } else {
      await viewOrEditTopicModal({
        recording: record,
        isCrossSubject: true,
      })

      list.refresh()
    }
  }

  async function deleteSelectedRecords({ all, selected }) {
    const dialogResult = await confirmDeleteModal()
    if (dialogResult && dialogResult.response) {
      const ids = selected.map((topic_record) => topic_record.id)

      await topicRecordBulk.destroy({ ids })
      all.refresh()
    }
  }

  const selectedItemActions = []
  if (currentUser.can("destroy", "market")) {
    selectedItemActions.push({
      label: "Delete",
      icon: TrashIcon,
      action: deleteSelectedRecords,
    })
  }

  const exportPath = `/markets/${marketId}/${pluralize(subjectType)}/topics/${
    topic.name
  }`

  return (
    <ObjectList
      subjectType={subjectType}
      parentSubjectType="market"
      parentSubjectId={marketId}
      topic={topic}
      availableViews={["table", "map", "compare"]}
      defaultView="table"
      enableLocationIndex={true}
      exportPath={exportPath}
      onItemClick={handleTopicRecordClick}
      selectedItemActions={selectedItemActions}
      rootUrl={rootUrl}
      canRedirectToRootUrl={canRedirectToRootUrl}
    />
  )
}

function SubjectTopicList({
  subjectType,
  marketId,
  topicName,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const topics = useTopics({ subjectType })
  const topic = topics.find((topic) => topic.name === topicName)

  return (
    <TopicObjectList
      subjectType={subjectType}
      marketId={marketId}
      topic={topic}
      rootUrl={rootUrl}
      canRedirectToRootUrl={canRedirectToRootUrl}
    />
  )
}

function AssetTopicList({
  marketId,
  topicName,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const topics = useTopics({ subjectType: "asset" })
  const topic = topics.find((topic) => topic.name === topicName)

  return (
    <TopicObjectList
      subjectType="asset"
      marketId={marketId}
      topic={topic}
      rootUrl={rootUrl}
      canRedirectToRootUrl={canRedirectToRootUrl}
    />
  )
}

export default function MarketTopicList({
  subjectType,
  marketId,
  route: routeProp,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const routeParams = useParams()
  const topicName = !!routeProp ? routeProp[0].key : routeParams.topicName

  if (subjectType === "asset") {
    return (
      <AssetTopicList
        marketId={marketId}
        topicName={topicName}
        rootUrl={rootUrl}
        canRedirectToRootUrl={canRedirectToRootUrl}
      />
    )
  } else {
    return (
      <SubjectTopicList
        subjectType={subjectType}
        marketId={marketId}
        topicName={topicName}
        rootUrl={rootUrl}
        canRedirectToRootUrl={canRedirectToRootUrl}
      />
    )
  }
}
